<template>
	<div class="content-page content-page--global-forms">
		<div class="content-page__heading" ref="globalFormsContentPageHeading">
			<div class="content-page__breadcrumb-title">
				<h2 class="content-page__title">Global Forms</h2>
			</div>
			<div class="content-page__actions">
				<ButtonGroup
					:disabled="!addFormDisabled"
					:title="
						!addFormDisabled
							? 'You don\'t have permission to perform this action.'
							: 'Select to go to add form.'
					"
				>
					<template #container>
						<Button
							:btnClick="callCodeManager"
							:labelText="'Code Manager'"
							v-if="hasSnippetPermissions"
						/>

						<MenuButton
							variantName="primary"
							:items="formCreateOptions"
							:placeholder="'New Form'"
							v-if="hasFormCreatePermission"
							v-model="formCreateValue"
							@change="addForm"
						/>
					</template>
				</ButtonGroup>
			</div>
		</div>
		<div
			class="results-toolbar results-toolbar--variant-seven"
			ref="globalFormsResultsToolBar"
		>
			<div class="results-toolbar__search">
				<Search
					variant="toggle-icons"
					v-model.lazy="filters.search"
					placeholder="Search by form name, description, or ID"
				/>
			</div>
			<div class="results-toolbar__filter">
				<DropdownV2
					v-model="filters.status"
					defaultValue="active"
					:options="statusOptions"
				/>
				<Dropdown
					name="status"
					defaultValue="All Type"
					:options="typeOptions"
					:showClearSelectedButton="false"
					v-model="filters.type"
				/>
			</div>
			<div class="results-toolbar__counter">
				<ResultCounter :counter="resultCounter" v-if="hasResults" />
			</div>
		</div>
		<Spinner :processor="{ loading: loading }" />
		<NoResults v-if="!loading && !hasResults" />
		<ScrollContainer
			v-if="hasResults"
			variant="infinity-scroll"
			:hasMoreItems="hasMoreItemsToLoad"
			@scrollEnd="handleScrollEnd"
		>
			<GlobalFormsList :source="formsList" ref="formsList" />
		</ScrollContainer>

		<CopyFormAddModal v-model="showModal" :items="forms" />
	</div>
</template>

<script>
import Search from '@/components/forms/search/search';
import ResultCounter from '@/site-components/result-counter/result-counter';
import ButtonGroup from '@/components/forms/button/button-group';
import MenuButton from '@/components/forms/button/menu-button';
import Button from '@/components/forms/button/button';
import CopyFormAddModal from '@/views/global-forms/copy-form-from-modal';
import Spinner from '@/components/visual/spinner/spinner';
import GlobalFormsList from '@/views/global-forms/global-forms-list.vue';
import Dropdown from '@/components/forms/dropdown/dropdown';
import DropdownV2 from '@/components/forms/dropdown/dropdown-v2.vue';
import ScrollContainer from '@/components/shells/scroll-container/scroll-container';
import NoResults from '@/site-components/no-results/no-results';
//vuex
import { mapState, mapActions } from 'vuex';

// libraries
import {
	removeObjectEmptyValues,
	removePropertiesWithSameValues
} from '@/utils/object';

export default {
	name: 'GlobalFormManager',
	components: {
		Search,
		ResultCounter,
		CopyFormAddModal,
		ScrollContainer,
		Button,
		MenuButton,
		ButtonGroup,
		Dropdown,
		DropdownV2,
		GlobalFormsList,
		Spinner,
		NoResults
	},
	props: {
		search: {
			type: String,
			default: ''
		},
		status: {
			type: String,
			default: ''
		},
		type: {
			type: String,
			default: ''
		},
		sortBy: {
			type: String
		},
		sortDirection: {
			type: String
		}
	},
	data() {
		return {
			formCreateValue: {
				text: 'New Form',
				value: 'default'
			},
			formCreateOptions: [
				{
					text: 'Copy Of',
					value: 'c'
				},
				{
					text: 'Laser Form',
					value: 'l'
				},
				{
					text: 'Impact Form',
					value: 'i'
				}
			],
			showModal: false,
			filters: {},
			page: 1,
			loading: true,
			loadingMore: false,
			statusOptions: [
				{
					text: 'All Statuses',
					value: 'all'
				},
				{
					text: 'Active',
					value: 'active'
				},
				{
					text: 'Inactive',
					value: 'inactive'
				}
			],
			typeOptions: [
				{
					text: 'All Types',
					value: 'all'
				},
				{
					text: 'Impact',
					value: 'i'
				},
				{
					text: 'Laser',
					value: 'l'
				}
			],
			defaultFilters: {
				search: undefined,
				sortby: 'date',
				sortdirection: 'desc',
				type: 'all',
				status: 'active'
			}
		};
	},
	beforeRouteEnter(to, from, next) {
		if (to.name === 'forms-manager') {
			to.meta.breadcrumb = [];
		}
		next();
	},
	computed: {
		...mapState('forms', ['reloadForms', 'forms']),
		addFormDisabled() {
			return this.validatePermission('forms.create');
		},
		hasSnippetPermissions() {
			return this.validatePermission('snippetsDraft.create');
		},
		hasFormCreatePermission() {
			return this.validatePermission('forms.create');
		},
		formsList() {
			return this.forms.items || [];
		},
		hasResults() {
			return this.formsList.length > 0 && !this.loading;
		},
		resultCounter() {
			return `${this.forms.items?.length} of ${this.forms.totalCount}`;
		},
		hasMoreItemsToLoad() {
			return this.page < this.forms.totalPageCount;
		}
	},
	methods: {
		...mapActions('forms', ['loadForms']),
		addForm(obj) {
			if (obj.value === 'l') {
				this.$router.push({
					path: '/forms/add/laser'
				});
			} else if (obj.value === 'i') {
				this.$router.push({
					path: '/forms/add/impact'
				});
			} else if (obj.value === 'c') {
				this.showModal = true;
			}
			this.formCreateValue = {
				text: 'New Form',
				value: 'default'
			};
		},
		async fetchData() {
			if (!this.loadingMore) {
				this.loading = true;
			}

			let query = removeObjectEmptyValues(this.filters);

			query.page = this.page;
			query.count = 100;

			if (query.status !== 'all') {
				query.active = query.status === 'active' ? true : false;
			}
			delete query.status;

			if (query.type === 'all') delete query.type;

			try {
				await this.loadForms({ ...query, saveOnStore: true });
			} catch (error) {
				console.log(error);
			} finally {
				this.loading = false;
				this.loadingMore = false;
			}
		},
		callCodeManager() {
			this.$router
				.push({
					path: '/forms/code'
				})
				.catch(() => {});
		},
		updateRoute() {
			let queries = removePropertiesWithSameValues(
				this.filters,
				this.defaultFilters
			);

			queries = removeObjectEmptyValues(queries);

			this.$router
				.push({
					path: `/forms`,
					query: queries
				})
				.catch(() => {});
		},
		handleScrollEnd() {
			if (this.hasMoreItemsToLoad) {
				this.loadingMore = true;

				this.page++;
				this.fetchData();
			}
		}
	},
	watch: {
		filters: {
			deep: true,
			handler() {
				this.page = 1;
				this.updateRoute();
			}
		},
		'$route.query': {
			immediate: true,
			handler(newQuery) {
				this.page = 1;
				this.filters = { ...this.defaultFilters, ...newQuery };
				this.fetchData();
			}
		},
		reloadForms: {
			deep: true,
			async handler(val) {
				if (val) {
					this.page = 1;
					await this.fetchData();
					this.$store.dispatch('forms/reloadForms', false);
				}
			}
		}
	},
	beforeRouteLeave(to, from, next) {
		if (
			to.name === 'form-history' ||
			to.name === 'impact-form-add-designer' ||
			to.name === 'impact-form-edit-designer' ||
			to.name === 'impact-form-copy-designer' ||
			to.name === 'laser-form-add-designer' ||
			to.name === 'laser-form-edit-designer' ||
			to.name === 'laser-form-copy-designer' ||
			to.name === 'code-manager'
		) {
			const breadcrumbMapping = {
				'impact-form-add-designer': 'Form Designer',
				'impact-form-edit-designer': 'Form Designer',
				'impact-form-copy-designer': 'Form Designer',
				'laser-form-add-designer': 'Form Designer',
				'laser-form-edit-designer': 'Form Designer',
				'laser-form-copy-designer': 'Form Designer',
				'form-history': 'History',
				'code-manager': 'Code Manager'
			};
			const getBreadcrumbName = key => breadcrumbMapping[key] || key;

			const breadcrumbName = getBreadcrumbName(to.name);

			to.meta.breadcrumb = [
				{
					name: 'Global Forms',
					data: { path: from.fullPath }
				},
				{ name: breadcrumbName }
			];
		}
		next();
	}
};
</script>

<style lang="scss" scoped>
/**
  * Global Forms
**/

::v-deep .results-toolbar__search {
	@include mq($from: md) {
		width: 310px;
	}
}

.content-page {
	&__heading {
		@include mq($until: sm-l) {
			flex-direction: column;
			align-items: unset;
		}
	}
	&__actions {
		@include mq($until: md) {
			margin-top: $gutter-md;
		}
	}
}
</style>
